import { Close, Rule } from "@mui/icons-material";
import {
    Alert,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    TextField,
    Tooltip,
    Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import { ChangeEvent, FormEvent, useContext, useEffect, useState } from "react";
import { Global_CTX } from "../../contexts/global-ctx";
import {
    Attendance_Service,
    Get_Attendance_Filter,
    IAttendance_Row,
} from "../../services/attendance/attendance-service";
import { User_Service } from "../../services/user/user-service";
import { Autocomplete } from "../Autocomplete/Autocomplete";
import { RUDTable } from "../Table/RUDTable";
import UserContext, { User_Type } from "../UserContext";
import SingleAttendanceHistory from "./SingleAttendanceHistory";
import User_Attendance_History from "./user-attendance-history";

const initial_filter: Omit<Get_Attendance_Filter, "page" | "limit"> = {
    organizationCode: "",
    apiKey: "",
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
};

const initial_date_range = {
    startDate: new Date().toISOString().split("T")[0],
    endDate: new Date().toISOString().split("T")[0],
};

export default function Attendance_Page() {
    const [filter, set_filter] = useState(initial_filter);
    const [date_range, set_date_range] = useState(initial_date_range);
    const [open, set_open] = useState(false);
    const { user } = useContext(UserContext);
    const { set_refetch_counter, refetch_counter } = useContext(Global_CTX);
    const [attendance_history, set_attendance_history] =
        useState<null | IAttendance_Row>(null);

    function handle_date_change(event: ChangeEvent<HTMLInputElement>) {
        const { name, value } = event.target;
        const change = { ...date_range, [name]: value };

        set_date_range(change);
    }

    function handle_apply_filter(event: FormEvent<HTMLFormElement>) {
        event.preventDefault();

        set_filter((f) => ({ ...f, ...date_range }));
        set_refetch_counter((n) => n + 1);
    }

    useEffect(() => {
        if (user?.user_type === User_Type.Admin) {
            set_filter((f) => ({
                ...f,
                organizationCode: user.organization.code,
                apiKey: user.organization.apiKey[0].key,
            }));

            set_refetch_counter((n) => n + 1);
        }
    }, [user?.user_type]);

    function calculateWorkingHours(checkIn?: string, checkOut?: string) {
        if (!checkIn || !checkOut) return "—";

        const start = new Date(checkIn).getTime();
        const end = new Date(checkOut).getTime();

        if (isNaN(start) || isNaN(end) || end <= start) return "—";

        const diffMs = end - start;

        const hours = Math.floor(diffMs / (1000 * 60 * 60));
        const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

        return `${hours}h ${minutes}m`;
    }

    function getAttendanceIds(row: any) {
        return row.ids;
    }

    return (
        <Box>
            <Typography
                pt={2}
                px={2}
                color={(theme) => theme.palette.primary.main}
                variant="h5"
            >
                Attendance
            </Typography>

            <Grid
                container
                p={2}
                spacing={1}
                component="form"
                onSubmit={handle_apply_filter}
            >
                {user?.user_type === User_Type.Superadmin && (
                    <Grid item xs={12} sm={6} md={4} lg={4}>
                        <Autocomplete
                            label="Organization"
                            labelKey="name"
                            api="/organization"
                            setOutput={(org) => {
                                set_filter((f) => ({
                                    ...f,
                                    organizationCode: org?.code || "",
                                    // api_key:
                                    //     org && org_api_key_map[org.code]
                                    //         ? org_api_key_map[org.code]
                                    //         : "",
                                }));
                            }}
                        />
                    </Grid>
                )}

                <Grid item xs={12} sm={6} md={4} lg={4}>
                    <Autocomplete
                        key={`branch-input`}
                        label="Branch"
                        labelKey="name"
                        setOutput={(branch) => {
                            set_filter((curr) => ({
                                ...curr,
                                branchId: branch?.id ?? "",
                            }));
                        }}
                        api="/organization/branch"
                        apiParams={{
                            organization:
                                user?.user_type === User_Type.Admin
                                    ? user.organization.code
                                    : "",
                        }}
                        request_headers={{
                            "x-org":
                                user?.user_type === User_Type.Admin
                                    ? user.organization.code
                                    : "",
                            authorization: `Bearer ${filter.apiKey}`,
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={4}>
                    <TextField
                        label="Start Date"
                        InputProps={{
                            type: "date",
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            minWidth: 200,
                            "& input::-webkit-calendar-picker-indicator": {
                                filter: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "invert(100%)"
                                        : undefined, // Adjust for desired color (e.g., green)
                            },
                        }}
                        value={date_range.startDate}
                        onChange={handle_date_change}
                        name="startDate"
                        fullWidth
                        required
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={4}>
                    <TextField
                        label="End Date"
                        InputProps={{
                            type: "date",
                        }}
                        InputLabelProps={{ shrink: true }}
                        sx={{
                            minWidth: 200,
                            "& input::-webkit-calendar-picker-indicator": {
                                filter: (theme) =>
                                    theme.palette.mode === "dark"
                                        ? "invert(100%)"
                                        : undefined,
                            },
                        }}
                        value={date_range.endDate}
                        onChange={handle_date_change}
                        name="endDate"
                        fullWidth
                        required
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={4}>
                    <Autocomplete
                        label="User"
                        labelKey="name"
                        setOutput={(user) => {
                            set_filter((curr) => ({
                                ...curr,
                                user: user?.pin ?? "",
                            }));
                        }}
                        api={User_Service.url}
                        request_headers={{
                            "x-org":
                                user?.user_type === User_Type.Admin
                                    ? user.organization.code
                                    : "",
                            authorization: `Bearer ${filter.apiKey}`,
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={4}>
                    <Autocomplete
                        key={"devices-filter"}
                        label="Device"
                        labelKey="name_serial"
                        setOutput={(device) => {
                            set_filter((curr) => ({
                                ...curr,
                                device: device?.serial ?? "",
                            }));
                        }}
                        processor={(opt) => ({
                            ...opt,
                            name_serial: `${opt.name} - (${opt.serial})`,
                        })}
                        api="/device"
                        request_headers={{
                            "x-org":
                                user?.user_type === User_Type.Admin
                                    ? user.organization.code
                                    : "",

                            authorization: `Bearer ${filter.apiKey}`,
                        }}
                    />
                </Grid>

                <Grid item xs={12} sm={6} md={4} lg={4}>
                    <Button
                        disabled={!date_range.startDate || !date_range.endDate}
                        variant="contained"
                        type="submit"
                        sx={{ height: "100%" }}
                        fullWidth
                    >
                        Search
                    </Button>
                </Grid>
            </Grid>

            {filter.organizationCode &&
                (!filter.startDate || !filter.endDate ? (
                    <Box px={2}>
                        <Alert severity="warning">
                            Provide a date range to view attendance record.
                        </Alert>
                    </Box>
                ) : (
                    <RUDTable
                        key={filter.user}
                        storageKey="attendance-page"
                        updatingAgents={[refetch_counter]}
                        getter={async (page, limit) => {
                            const [data, error] =
                                await Attendance_Service.get_attendance(
                                    {
                                        page,
                                        limit,
                                        ...filter,
                                    },
                                    filter.apiKey as any
                                );

                            if (error) return [data, error];

                            if (!filter.user && data?.success) {
                                console.log({ data });

                                data.rows = ((data as any)?.expanded_rows ??
                                    []) as any[];
                            }

                            return [data, error];
                        }}
                        rowsPreprocessor={(row) =>
                            filter.user
                                ? {
                                      ...row,
                                      user_name: row?.user?.name,
                                      user_pin: row?.user?.pin,
                                      branch_name: row?.branch?.name,
                                      organization_name:
                                          row?.organization?.name,
                                      device_serial: row?.device?.serial,
                                      organization: "",
                                  }
                                : {
                                      ...row,
                                      user_name: row?.user?.name,
                                      user_pin: row?.user?.pin,
                                      branch_name: row?.branch?.name,
                                      organization_name:
                                          row?.organization?.name,
                                      device_serial: row?.device?.serial,
                                      total_hours: calculateWorkingHours(
                                          row?.CheckIn,
                                          row?.CheckOut
                                      ),
                                  }
                        }
                        ops={{
                            read: "",
                        }}
                        readables={{
                            ...(!filter.user
                                ? { "Attendance IDs": "Attendance IDs" }
                                : {}),
                            user_name: "Name",
                            user_pin: "Pin",
                            branch_name: "Branch",
                            device_serial: "Device Serial",
                            ...(filter.user
                                ? { "Attendance History": "Attendance History" }
                                : {
                                      CheckIn: "Check-in",
                                      CheckOut: "Check-out",
                                  }),
                            ...(!filter.user
                                ? { total_hours: "Total Hours" }
                                : {}),

                            ...(!filter.user
                                ? {
                                      "Attendance History Day Wise":
                                          "Attendance History Day Wise",
                                  }
                                : {}),
                        }}
                        customCols={[
                            {
                                header: "Attendance History",
                                content: (row) => (
                                    <Tooltip title="Attendance History">
                                        <IconButton
                                            size="small"
                                            onClick={() => {
                                                set_open(true);
                                                set_attendance_history(row);
                                            }}
                                        >
                                            <Rule fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                ),
                            },

                            {
                                header: "Attendance History Day Wise",
                                content: (row) => {
                                    return (
                                        <SingleAttendanceHistory row={row} />
                                    );
                                },
                            },

                            // {
                            //     header: "Attendance IDs",
                            //     content: (row) => {
                            //         const ids = getAttendanceIds(row);
                            //         return (
                            //             <Typography variant="body2">
                            //                 {ids?.length ? ids.join(", ") : "—"}
                            //             </Typography>
                            //         );
                            //     },
                            // },

                            ...(!filter.user
                                ? [
                                      {
                                          header: "Attendance IDs",
                                          content: (row: any) => {
                                              const ids = getAttendanceIds(row);
                                              return (
                                                  <Typography variant="body2">
                                                      {ids?.length
                                                          ? ids.join(", ")
                                                          : "—"}
                                                  </Typography>
                                              );
                                          },
                                      },
                                  ]
                                : []),
                        ]}
                    />
                ))}

            {/* <Dialog
                open={open && attendance_history !== null}
                onClose={() => {
                    set_open(false);

                    setTimeout(() => {
                        set_attendance_history(null);
                    }, 200);
                }}
                fullScreen
            >
                <DialogTitle>
                    {`Attendance History for ${attendance_history?.user?.name}`}
                </DialogTitle>

                <DialogContent>
                    {attendance_history && (
                        <User_Attendance_History history={attendance_history} />
                    )}
                </DialogContent>
            </Dialog> */}
            <Dialog
                open={open && attendance_history !== null}
                onClose={() => {
                    set_open(false);
                    setTimeout(() => {
                        set_attendance_history(null);
                    }, 200);
                }}
                fullScreen
            >
                <DialogTitle
                    sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        paddingLeft: 2,
                    }}
                >
                    {`Attendance History for ${attendance_history?.user?.name}`}
                    <IconButton
                        edge="end"
                        color="inherit"
                        onClick={() => {
                            set_open(false);
                            setTimeout(() => set_attendance_history(null), 200);
                        }}
                        aria-label="close"
                    >
                        <Close />
                    </IconButton>
                </DialogTitle>

                <DialogContent>
                    {attendance_history && (
                        <User_Attendance_History history={attendance_history} />
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
}
